import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { getLinkStorageFileByTime } from '../../socket/playback';
import './index.scss';
import { PlaybackController } from './Controller';
import { STREAM_MODE, timeToSeconds } from './utils';
import { beeXLogger } from '../../helpers/logger';
import { removeViewingRecordId } from '../../redux/recovery/action';
import { FilePlayer } from './FilePlayer';
import { LoadingOverlay } from './LoadingOverlay';
import { useStreamPlayer } from './hooks/useStreamPlayer';
import { useDownloader } from './hooks/useDownloader';
import { usePingPlayback } from './hooks/usePingPlayback';
import { useFullscreen } from './hooks/useFullscreen';

const PlaybackPlayer = ({
  serial,
  queryTime,
  thumbnail,
  allowFullScreen = true,
  variantPage = '',
  src,
  logContent,
  logSerial,
  handleReportActionLog,
  permission,
  autoPlay = false,
  fromTime,
  toTime,
  recordId,
  selectedDate,
  type = '',
  duration,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const refFilePlayer = useRef(null);
  const isMountedRef = useRef(true);
  const isSeekingRef = useRef(false);
  const progress = useRef();
  const videoDurationRef = useRef(0);
  const playbackStreamRef = useRef(null);
  const isPlayingRef = useRef(false);
  const hlsRef = useRef(null);
  const hlsRetryRef = useRef({
    network: 0,
    media: 0,
    lastProgressAt: Date.now(),
  });

  const manifestRetryTimerRef = useRef(null);

  const HLS_MAX_NETWORK_RETRY = 2;
  const HLS_MAX_MEDIA_RECOVER = 1;
  const HLS_STALL_TIMEOUT_MS = 10000;

  const { namespace, dataUser } = useSelector((state) => state);
  const dispatch = useDispatch();

  const [activeQueryTime, setActiveQueryTime] = useState(queryTime);
  const [playerMode, setPlayerMode] = useState('file');
  const [video, setVideo] = useState({
    playing: false,
    played: 0,
    duration: 0,
    playbackRate: 1,
    ended: false,
  });

  const { isFullScreen, containerRef, handleFullscreen } = useFullscreen();

  const {
    playbackStream,
    output,
    streamMode,
    isLoading,
    setIsLoading,
    loadingStage,
    videoError,
    setVideoError,
    resetStreamState,
    restartPlayback,
    retryPlaybackByCurrentLink,
    retryNormalOnceOrFallbackTranscode,
    requestStreamRefetch,
    handleProgress,
    handleReady,
    handleStart,
  } = useStreamPlayer({
    serial,
    namespace,
    autoPlay,
    activeQueryTime,
    selectedDate,
    playerMode,
    isMountedRef,
    isSeekingRef,
    videoRef,
    canvasRef,
    playerRef,
    setVideo,
    video,
    videoDurationRef,
  });

  const {
    isDownloading,
    downloadProgress,
    isRequestingDownload,
    onDownloadDirectClick,
  } = useDownloader({
    type,
    src,
    logContent,
    logSerial,
    namespace,
    serial,
    fromTime,
    toTime,
    handleReportActionLog,
    selectedDate,
  });

  usePingPlayback({ serial, namespace, isPlayingRef, playbackStreamRef });

  useEffect(() => {
    playbackStreamRef.current = playbackStream;
  }, [playbackStream]);
  useEffect(() => {
    isPlayingRef.current = video.playing;
  }, [video.playing]);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      clearTimeout(manifestRetryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setActiveQueryTime(queryTime);
  }, [queryTime]);

  useEffect(() => {
    if (recordId) dispatch(removeViewingRecordId(recordId));
  }, [dispatch, recordId]);

  useEffect(() => {
    if (toTime && queryTime && variantPage === 'camera') {
      const toSeconds = timeToSeconds(toTime);
      const fromSeconds = timeToSeconds(moment(queryTime).format('HH:mm:ss'));
      if (toSeconds - fromSeconds > 0) {
        const d = toSeconds - fromSeconds;
        videoDurationRef.current = d;
        beeXLogger.debug('DURATION 1: ', d);
        setVideo((p) => ({ ...p, duration: d }));
      }
    } else {
      videoDurationRef.current = duration;
      beeXLogger.debug('DURATION 2: ', duration);
      setVideo((p) => ({ ...p, duration }));
    }
  }, [queryTime, variantPage, toTime, duration]);

  const handlePlay = () => setVideo((p) => ({ ...p, playing: true }));
  const handlePause = () => setVideo((p) => ({ ...p, playing: false }));
  const handleEnded = () =>
    setVideo((p) => ({ ...p, playing: false, ended: true }));

  const handleDuration = useCallback(
    (d) => {
      beeXLogger.debug('handleDuration:', d);
      if (playerMode === 'stream') return;
      if (!d) return;
      videoDurationRef.current = d;
      beeXLogger.debug('DURATION 3: ', d);
      setVideo((p) => ({ ...p, duration: d }));
    },
    [playerMode],
  );

  const handleSpeedChange = useCallback((playbackRate) => {
    setVideo((p) => ({ ...p, playbackRate }));
  }, []);

  const handleReplay = useCallback(() => {
    setVideo((p) => ({ ...p, played: 0, ended: false, playing: true }));
    if (videoError || output || playbackStream) {
      restartPlayback();
      return;
    }
    resetStreamState('handleReplay without output/stream');
    setActiveQueryTime(queryTime);
  }, [
    videoError,
    output,
    playbackStream,
    restartPlayback,
    resetStreamState,
    queryTime,
  ]);

  const handleSeekChange = useCallback(
    (event) => {
      if (!progress.current || !video.duration) return;

      const raw =
        (event.clientX - progress.current.getBoundingClientRect().left) /
        progress.current.offsetWidth;
      const clampedPlayed = Math.min(1, Math.max(0, raw));

      if (playerMode !== 'file') {
        const seekOffsetSeconds = clampedPlayed * video.duration;
        const newQueryTime = moment(queryTime)
          .add(seekOffsetSeconds, 'seconds')
          .format('YYYY-MM-DD HH:mm:ss');
        beeXLogger.debug(
          'handleSeekChange → newQueryTime:',
          newQueryTime,
          'offset:',
          seekOffsetSeconds,
        );
        isSeekingRef.current = true;
        setVideo((p) => ({ ...p, played: clampedPlayed, playing: true }));
        resetStreamState('handleSeekChange');
        setActiveQueryTime(newQueryTime);
      } else {
        if (refFilePlayer.current) {
          refFilePlayer.current.currentTime = clampedPlayed * video.duration;
        }
        setVideo((p) => ({ ...p, played: clampedPlayed }));
      }
    },
    [queryTime, video.duration, resetStreamState, playerMode],
  );

  const fallbackToStreamMode = useCallback(() => {
    if (!isMountedRef.current) return;
    resetStreamState('fallbackToStreamMode');
    setVideoError(null);
    setPlayerMode('stream');
    setIsLoading(true);
  }, [resetStreamState, setVideoError, setIsLoading]);

  useEffect(() => {
    if (playerMode !== 'file') return;

    if (serial && namespace && !playbackStream && type !== 'on_gateway') {
      setIsLoading(true);
      let cancelled = false;

      getLinkStorageFileByTime(
        {
          namespace,
          deviceSerial: serial,
          queryTime: moment(queryTime)
            .subtract(3, 'seconds')
            .format('YYYY-MM-DD HH:mm:ss'),
          ipAddress: '0.0.0.0',
        },
        10000,
      )
        .then((res) => {
          if (cancelled) return;
          if (!res?.secureLink) {
            fallbackToStreamMode();
            return;
          }
          setVideo((prev) => ({
            ...prev,
            played: 0,
            ended: false,
            src: res.secureLink,
            fileConfig: {
              startSecond: res.startSecond || 0,
              ipAddress: res.ipAddress || '',
              recordId: res.recordId || null,
            },
          }));
        })
        .catch((err) => {
          if (cancelled) return;
          beeXLogger.error('GET PLAYBACK FILE ERROR:', err);
          fallbackToStreamMode();
        });

      return () => {
        cancelled = true;
      };
    }

    if (autoPlay && !playbackStream) {
      fallbackToStreamMode();
    }
  }, [
    namespace,
    serial,
    queryTime,
    playbackStream,
    fallbackToStreamMode,
    type,
    autoPlay,
    playerMode,
    setIsLoading,
  ]);

  useEffect(() => {
    if (playerMode !== 'stream') return;
    if (typeof output !== 'string') return;
    const videoEl = playerRef.current;
    if (!videoEl) return;
    videoEl.playbackRate = video.playbackRate || 1;
  }, [output, playerMode, video.playbackRate]);

  useEffect(() => {
    if (playerMode !== 'stream') return;
    if (typeof output !== 'string') return;
    const videoEl = playerRef.current;
    if (!videoEl) return;
    if (video.playing || autoPlay) {
      videoEl.play().catch(() => { });
    } else if (typeof videoEl.pause === 'function') {
      videoEl.pause();
    }
  }, [output, playerMode, video.playing, autoPlay]);

  useEffect(() => {
    if (playerMode !== 'stream') return;
    if (typeof output !== 'string') return;
    const videoEl = playerRef.current;
    if (!videoEl) return;

    const Hls = window?.Hls;
    const resetHlsRetry = () => {
      hlsRetryRef.current = {
        network: 0,
        media: 0,
        lastProgressAt: Date.now(),
      };
    };

    const destroyHls = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };

    const handleFatal = (err, reason) => {
      if (!isMountedRef.current) return;
      if (streamMode === STREAM_MODE.TRANSCODE) {
        retryPlaybackByCurrentLink(output);
        return;
      }
      const handled = retryNormalOnceOrFallbackTranscode(
        err,
        null,
        reason,
        () => retryPlaybackByCurrentLink(output),
      );
      setIsLoading(handled);
    };

    const handleManifestRefetch = (err, reason) => {
      if (!isMountedRef.current) return;

      destroyHls();
      setIsLoading(true);

      clearTimeout(manifestRetryTimerRef.current);
      manifestRetryTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;

        retryNormalOnceOrFallbackTranscode(err, null, reason, () =>
          requestStreamRefetch('hls-manifest'),
        );
      }, 1500);
    };

    const handleNetworkRetry = (err) => {
      const retry = hlsRetryRef.current;
      if (retry.network >= HLS_MAX_NETWORK_RETRY) {
        handleFatal(err, 'HLS network error');
        return false;
      }
      retry.network += 1;
      setIsLoading(true);
      if (hlsRef.current) hlsRef.current.startLoad();
      return true;
    };

    const handleMediaRecover = (err) => {
      const retry = hlsRetryRef.current;
      if (retry.media >= HLS_MAX_MEDIA_RECOVER) {
        handleFatal(err, 'HLS media error');
        return false;
      }
      retry.media += 1;
      if (hlsRef.current) hlsRef.current.recoverMediaError();
      return true;
    };

    const onTimeUpdate = () => {
      hlsRetryRef.current.lastProgressAt = Date.now();
      handleProgress(videoEl.currentTime || 0);
    };

    const onWaiting = () => {
      const last = hlsRetryRef.current.lastProgressAt || 0;
      if (Date.now() - last > HLS_STALL_TIMEOUT_MS) {
        handleNetworkRetry(new Error('HLS stalled'));
      }
    };

    const onError = (err) => {
      handleFatal(err, 'HLS video element error');
    };

    const onCanPlay = () => {
      handleReady();
    };

    resetHlsRetry();
    setIsLoading(true);

    if (Hls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,

        lowLatencyMode: false,
        liveSyncDurationCount: 4,
        liveMaxLatencyDurationCount: 8,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        backBufferLength: 30,

        maxBufferHole: 0.5,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 5,

        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 1000,
        manifestLoadingTimeOut: 20000,
        manifestLoadingMaxRetry: 4,
      });
      hlsRef.current = hls;
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(output);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        handleReady();
        if (isPlayingRef.current || autoPlay) {
          videoEl.play().catch(() => { });
        }
      });
      hls.on(Hls.Events.ERROR, (event, data) => {
        beeXLogger.error('HLS ERROR ======>>', data);
        beeXLogger.error('HLS MANIFEST ERROR:', {
          url: data?.url || data?.context?.url,
          code: data?.response?.code,
          text: data?.response?.text,
          details: data?.details,
          fatal: data?.fatal,
        });
        if (!data) return;
        const responseCode =
          data?.response?.code || data?.response?.status || null;
        const isManifestError =
          data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
          `${data?.details || ''}`.toLowerCase().includes('manifest');
        const isBufferSeekOverHole =
          data?.details === Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE ||
          `${data?.details || ''}`.toLowerCase().includes('bufferseekoverhole');
        const isBufferStalledError =
          data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR ||
          `${data?.details || ''}`.toLowerCase().includes('bufferstalled');
        const isBadStatus =
          responseCode &&
          Number(responseCode) >= 400 &&
          Number(responseCode) < 600;

        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (isManifestError || isBadStatus) {
              handleManifestRefetch(data, 'HLS manifest error');
            } else {
              handleNetworkRetry(data);
            }
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            if (isBufferSeekOverHole || isBufferStalledError) {
              beeXLogger.debug('Ignoring benign HLS media error:', {
                details: data?.details,
                reason: data?.reason,
              });
              return;
            }
            handleMediaRecover(data);
            return;
          }
          handleFatal(data, 'HLS fatal error');
          return;
        }

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (isManifestError || isBadStatus) {
            handleManifestRefetch(data, 'HLS manifest error');
          } else {
            handleNetworkRetry(data);
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          beeXLogger.debug('Ignoring non-fatal HLS media error:', {
            details: data?.details,
            reason: data?.reason,
          });
        }
      });
    } else {
      videoEl.src = output;
      videoEl.load();
      videoEl.play().catch(() => { });
    }

    videoEl.addEventListener('timeupdate', onTimeUpdate);
    videoEl.addEventListener('waiting', onWaiting);
    videoEl.addEventListener('stalled', onWaiting);
    videoEl.addEventListener('error', onError);
    videoEl.addEventListener('canplay', onCanPlay);
    videoEl.addEventListener('playing', handleStart);

    return () => {
      videoEl.removeEventListener('timeupdate', onTimeUpdate);
      videoEl.removeEventListener('waiting', onWaiting);
      videoEl.removeEventListener('stalled', onWaiting);
      videoEl.removeEventListener('error', onError);
      videoEl.removeEventListener('canplay', onCanPlay);
      videoEl.removeEventListener('playing', handleStart);

      clearTimeout(manifestRetryTimerRef.current);
      destroyHls();
    };
  }, [
    output,
    playerMode,
    streamMode,
    autoPlay,
    handleReady,
    handleStart,
    handleProgress,
    retryPlaybackByCurrentLink,
    retryNormalOnceOrFallbackTranscode,
    requestStreamRefetch,
    setIsLoading,
  ]);

  const renderPlayer = () => {
    if (playerMode === 'file') {
      return (
        <FilePlayer
          src={video.src}
          handleFullscreen={handleFullscreen}
          onPlay={handlePlay}
          onPause={handlePause}
          onDurationChange={handleDuration}
          onEnded={handleEnded}
          onProgress={handleProgress}
          onError={fallbackToStreamMode}
          thumbnail={thumbnail}
          refVideo={refFilePlayer}
          loading={video.loading}
          playbackRate={video.playbackRate}
          startSecond={video?.fileConfig?.startSecond}
        />
      );
    }

    return (
      <>
        {typeof output === 'string' && (
          <video
            ref={playerRef}
            style={{ width: '100%', height: '100%' }}
            autoPlay
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
          />
        )}

        {output instanceof MediaStream && (
          <video
            ref={videoRef}
            style={{ width: '100%', height: '100%' }}
            autoPlay
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
            onError={(err) => {
              retryNormalOnceOrFallbackTranscode(
                err,
                null,
                'Failed to view playback stream',
                () => retryPlaybackByCurrentLink(null),
              );
            }}
          />
        )}

        {!output && thumbnail && (
          <img
            src={thumbnail}
            alt=""
            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
          />
        )}
      </>
    );
  };

  return (
    <div className={`playback-player-wrapper ${autoPlay ? 'playing' : ''}`}>
      <div
        className={`playback-player ${isFullScreen ? 'fullscreen' : ''}`}
        ref={containerRef}
      >
        <LoadingOverlay
          isLoading={isLoading}
          loadingStage={loadingStage}
          playerMode={playerMode}
          ended={video.ended}
        />

        {videoError ? (
          <div className="video-error">{videoError}</div>
        ) : (
          renderPlayer()
        )}

        {autoPlay && (
          <PlaybackController
            variantPage={variantPage}
            video={video}
            output={output}
            progress={progress}
            canvasRef={canvasRef}
            isFullScreen={isFullScreen}
            isDownloading={isDownloading}
            allowFullScreen={allowFullScreen}
            permission={permission}
            onPlay={handlePlay}
            onPause={handlePause}
            onSeekChange={handleSeekChange}
            onSpeedChange={handleSpeedChange}
            onDownload={onDownloadDirectClick}
            onFullscreen={handleFullscreen}
            onReplay={handleReplay}
            streamMode={streamMode}
            downloadProgress={downloadProgress}
            isRequestingDownload={isRequestingDownload}
            type={type}
            hasError={!!videoError}
          />
        )}

        {!!(dataUser?.enableWatermark && video.playing) && (
          <div className="watermark-container">
            {[...Array(8)].map((_, i) => (
              <p
                key={i}
                style={{ transform: 'rotate(330deg)', opacity: '0.6' }}
              >
                {dataUser.phoneNumber}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaybackPlayer;
